/**
 * Shared utilities for toolbar button components
 * 
 * This module provides common functionality for button-like components
 * to reduce code duplication and ensure consistent behavior.
 */

import { ComponentEventDetail } from '../../types/index';

type QdsRuntimeEnv = {
  cdnBaseUrl?: unknown;
  CDN_BASE_URL?: unknown;
};

type QdsAssetBaseGlobal = typeof globalThis & {
  QDS_ASSET_BASE_URL?: unknown;
  CDN_BASE_URL?: unknown;
  env?: QdsRuntimeEnv;
};

const DEFAULT_QDS_ASSET_BASE_URL = 'https://cdn.q360.ai/assets/';

function normalizeAssetBaseUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const fallbackBase = typeof window !== 'undefined' && window.location
      ? window.location.href
      : 'http://localhost/';
    const url = new URL(trimmed, fallbackBase);
    const normalizedPath = url.pathname.endsWith('/dev/assets/')
      ? url.pathname
      : `${url.pathname.replace(/\/+$/, '')}/assets/`;

    url.pathname = normalizedPath;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch (error) {
    console.debug('Could not normalize QDS asset base URL:', error);
    return null;
  }
}

function getConfiguredAssetBaseUrl(): string | null {
  try {
    const globalAssetBase = (globalThis as QdsAssetBaseGlobal).QDS_ASSET_BASE_URL;
    if (typeof globalAssetBase === 'string') {
      const normalizedGlobal = normalizeAssetBaseUrl(globalAssetBase);
      if (normalizedGlobal) {
        return normalizedGlobal;
      }
    }
  } catch (error) {
    console.debug('Could not read global QDS asset base URL:', error);
  }

  try {
    const documentAssetBase = document.documentElement?.getAttribute('data-qds-asset-base-url');
    if (documentAssetBase) {
      const normalizedDocument = normalizeAssetBaseUrl(documentAssetBase);
      if (normalizedDocument) {
        return normalizedDocument;
      }
    }
  } catch (error) {
    console.debug('Could not read document QDS asset base URL:', error);
  }

  try {
    const metaAssetBase = document.querySelector<HTMLMetaElement>('meta[name="qds-asset-base-url"]')?.content;
    if (metaAssetBase) {
      const normalizedMeta = normalizeAssetBaseUrl(metaAssetBase);
      if (normalizedMeta) {
        return normalizedMeta;
      }
    }
  } catch (error) {
    console.debug('Could not read meta QDS asset base URL:', error);
  }

  try {
    const runtimeEnv = (globalThis as QdsAssetBaseGlobal).env;
    let envAssetBase: string | null = null;

    if (typeof runtimeEnv?.cdnBaseUrl === 'string') {
      envAssetBase = runtimeEnv.cdnBaseUrl;
    } else if (typeof runtimeEnv?.CDN_BASE_URL === 'string') {
      envAssetBase = runtimeEnv.CDN_BASE_URL;
    } else if (typeof (globalThis as QdsAssetBaseGlobal).CDN_BASE_URL === 'string') {
      envAssetBase = (globalThis as QdsAssetBaseGlobal).CDN_BASE_URL as string;
    }

    if (envAssetBase) {
      const normalizedEnv = normalizeAssetBaseUrl(envAssetBase);
      if (normalizedEnv) {
        return normalizedEnv;
      }
    }
  } catch (error) {
    console.debug('Could not read runtime CDN base URL:', error);
  }

  if (typeof document === 'undefined') {
    return null;
  }

  return null;
}

function getModuleHostedAssetBaseUrl(): string | null {
  try {
    const moduleUrl = new URL(import.meta.url);
    // Regex markers remain stable when the site rewrites absolute asset URLs.
    let bundledMarkerIndex = moduleUrl.pathname.search(/\/assets\/components\//);
    // Standalone entrypoints share this helper through emitted ESM chunks.
    if (bundledMarkerIndex === -1) bundledMarkerIndex = moduleUrl.pathname.search(/\/assets\/chunks\//);

    if (bundledMarkerIndex === -1) {
      return null;
    }

    const assetRootPath = `${moduleUrl.pathname.slice(0, bundledMarkerIndex)}/assets/`;
    return new URL(assetRootPath, moduleUrl).toString();
  } catch (error) {
    console.debug('Could not derive hosted QDS asset base URL:', error);
    return null;
  }
}

function getSourceTreeAssetBaseUrl(): string | null {
  try {
    const moduleUrl = new URL(import.meta.url);
    const sourceMarker = '/src/components/';
    const sourceMarkerIndex = moduleUrl.pathname.indexOf(sourceMarker);

    if (sourceMarkerIndex === -1) {
      return null;
    }

    const assetRootPath = `${moduleUrl.pathname.slice(0, sourceMarkerIndex)}/content/assets/`;
    return new URL(assetRootPath, moduleUrl).toString();
  } catch (error) {
    console.debug('Could not derive source-tree QDS asset base URL:', error);
    return null;
  }
}

export function getQdsAssetBaseUrl(): string {
  return getConfiguredAssetBaseUrl()
    || getModuleHostedAssetBaseUrl()
    || getSourceTreeAssetBaseUrl()
    || DEFAULT_QDS_ASSET_BASE_URL;
}

function resolveAssetUrl(assetPath: string): string {
  const normalizedAssetPath = assetPath.replace(/^\/+/, '');
  return new URL(normalizedAssetPath, getQdsAssetBaseUrl()).toString();
}

export function getBaseCssUrl(): string {
  return resolveAssetUrl('css/q-base.css');
}

export function getPrimeIconsCssUrl(): string {
  return resolveAssetUrl('vendor/primeicons/primeicons.css');
}

export function getPrimeIconsStyleImport(): string {
  return `@import "${getPrimeIconsCssUrl()}";`;
}

const PRIME_ICONS_DOCUMENT_STYLE_ID = 'qds-primeicons-styles';
const PRIME_ICONS_SHADOW_STYLE_ID = 'qds-primeicons-shadow-styles';
let primeIconsCssPromise: Promise<string> | null = null;
let primeIconsStyleSheet: CSSStyleSheet | null = null;

function makePrimeIconsUrlsAbsolute(css: string): string {
  const fontBaseUrl = new URL('vendor/primeicons/fonts/', getQdsAssetBaseUrl()).toString();
  return css.replace(/url\((['"]?)\.\/fonts\//g, `url($1${fontBaseUrl}`);
}

function loadPrimeIconsCss(): Promise<string> {
  if (!primeIconsCssPromise) {
    primeIconsCssPromise = fetch(getPrimeIconsCssUrl())
      .then(response => {
        if (!response.ok) {
          throw new Error(`Unable to load PrimeIcons: ${response.status} ${response.statusText}`);
        }

        return response.text();
      })
      .then(makePrimeIconsUrlsAbsolute)
      .catch(error => {
        primeIconsCssPromise = null;
        throw error;
      });
  }

  return primeIconsCssPromise;
}

function addPrimeIconsToDocument(css: string): void {
  if (typeof document === 'undefined' || document.getElementById(PRIME_ICONS_DOCUMENT_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = PRIME_ICONS_DOCUMENT_STYLE_ID;
  style.textContent = css;
  document.head.appendChild(style);
}

/**
 * Loads PrimeIcons once, then shares the stylesheet with component shadow roots.
 * This avoids an external CSS @import for every rendered icon component.
 */
export async function adoptPrimeIconsStyles(shadowRoot: ShadowRoot): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined'
    || window.navigator.userAgent.includes('Node.js') || window.navigator.userAgent.includes('jsdom')) {
    return;
  }

  try {
    const css = await loadPrimeIconsCss();
    addPrimeIconsToDocument(css);

    const supportsConstructableSheets = typeof CSSStyleSheet !== 'undefined'
      && 'replaceSync' in CSSStyleSheet.prototype
      && 'adoptedStyleSheets' in shadowRoot;

    if (supportsConstructableSheets) {
      if (!primeIconsStyleSheet) {
        primeIconsStyleSheet = new CSSStyleSheet();
        primeIconsStyleSheet.replaceSync(css);
      }

      if (!shadowRoot.adoptedStyleSheets.includes(primeIconsStyleSheet)) {
        shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, primeIconsStyleSheet];
      }
      return;
    }

    if (!shadowRoot.querySelector(`#${PRIME_ICONS_SHADOW_STYLE_ID}`)) {
      const style = document.createElement('style');
      style.id = PRIME_ICONS_SHADOW_STYLE_ID;
      style.textContent = css;
      shadowRoot.prepend(style);
    }
  } catch (error) {
    console.debug('Could not load shared PrimeIcons styles:', error);
  }
}

/**
 * Ensures that the base CSS file is loaded
 * Will only add the CSS link if it hasn't been added already
 */
export function loadBaseCSS(): void {
  try {
    // Skip in test environments
    if (typeof window !== 'undefined' && typeof document !== 'undefined' && 
        !window.navigator.userAgent.includes('Node.js') && 
        !window.navigator.userAgent.includes('jsdom')) {
      
      // Only load if not already loaded
      if (!document.getElementById('qds-base-styles')) {
        const linkElem = document.createElement('link');
        linkElem.id = 'qds-base-styles';
        linkElem.rel = 'stylesheet';
        linkElem.href = getBaseCssUrl();
        document.head.appendChild(linkElem);
      }
    }
  } catch (error) {
    // Silently fail in test environments or when CSS can't be loaded
    console.debug('Could not load QDS base styles: ', error);
  }
}

/**
 * Base styles for toolbar button components
 * Provides consistent appearance for all button-like components
 * Uses theme variables for colors to support dark mode
 * 
 * @returns {string} CSS styles as a string
 */
export function getBaseButtonStyles(): string {
  return `
    :host {
      display: inline-block;
    }

    button {
      position: relative;
      width: var(--button-control-size, 44px);
      height: var(--button-control-size, 44px);
      min-width: var(--button-control-size, 44px);
      min-height: var(--button-control-size, 44px);
      border: none;
      background: transparent;
      cursor: pointer;
      border-radius: var(--theme-radius-sm, 4px);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--icon-color-primary, var(--button-color, var(--theme-text-primary)));
      padding: 0;
      transition: background-color var(--transition-speed-fast, 0.15s),
                  color var(--transition-speed-fast, 0.15s),
                  box-shadow var(--transition-speed-fast, 0.15s);
    }

    button:hover {
      background-color: var(--button-hover-bg, var(--theme-background-hover));
    }

    button:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--button-focus-ring);
    }

    .icon {
      font-size: var(--icon-size, 20px);
      color: currentColor;
    }
  `;
}

/**
 * Creates a standard button component template with an icon
 * 
 * @param {string} iconClass - The icon class to use (PrimeIcons)
 * @param {string} styles - Additional CSS styles to include
 * @returns {string} HTML template string
 */
export function createButtonTemplate(iconClass: string, styles: string = ''): string {
  return `
    <style>
      ${getBaseButtonStyles()}
      ${styles}
    </style>
    
    <button part="button">
      <i class="pi ${iconClass} icon" part="icon"></i>
    </button>
  `;
}

/**
 * Standard event handling setup for button components
 * 
 * @param {HTMLElement} component - The web component instance
 * @param {string} eventName - Name of the event to dispatch on click (defaults to 'click')
 * @param {object} eventDetail - Optional detail object to include with the event
 * @returns {Function} Cleanup function to remove event listeners
 */
export function setupButtonEvents(
  component: HTMLElement, 
  eventName: string = 'click', 
  eventDetail: ComponentEventDetail = { component }
): () => void {
  const button = component.shadowRoot?.querySelector('button');
  
  // Skip if button isn't found
  if (!button) return () => {};
  
  const handleClick = (event: MouseEvent) => {
    // Dispatch a custom event when clicked
    component.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      composed: true,
      detail: eventDetail
    }));
  };
  
  // Add click event listener
  button.addEventListener('click', handleClick);
  
  // Return a cleanup function
  return () => {
    button.removeEventListener('click', handleClick);
  };
}

/**
 * Helper to create a base component class with standard button behavior
 * 
 * @param {string} iconClass - The icon class to use (from PrimeIcons)
 * @param {string} additionalStyles - Any additional CSS styles to include
 * @returns {class} A component class with standard button behavior
 */
export function createBaseButtonComponent(iconClass: string, additionalStyles: string = ''): typeof HTMLElement {
  // Load base CSS when component is created
  loadBaseCSS();
  
  return class BaseButtonComponent extends HTMLElement {
    private _cleanup?: () => void;
    
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.render(iconClass, additionalStyles);
    }
    
    connectedCallback(): void {
      if (!this.isConnected) return;
      this._cleanup = setupButtonEvents(this);
    }
    
    disconnectedCallback(): void {
      if (this._cleanup) {
        this._cleanup();
        this._cleanup = undefined;
      }
    }
    
    render(iconClass: string, additionalStyles: string): void {
      if (this.shadowRoot) {
        this.shadowRoot.innerHTML = createButtonTemplate(iconClass, additionalStyles);
        void adoptPrimeIconsStyles(this.shadowRoot);
      }
    }
  };
}
